/** Market intelligence ranking (no /100 scores). */

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

export function estimatedMidMrr(opp) {
  const min = Number(opp?.estimated_mrr_min) || 0;
  const max = Number(opp?.estimated_mrr_max) || 0;
  return (min + max) / 2;
}

/** USD-ish magnitude for ranking when verified benchmark missing (EUR mid treated as comparable scale). */
export function effectiveMrrUsd(opp) {
  const v = opp?.display_mrr_usd ?? opp?.market_avg_mrr_usd;
  if (v != null && Number(v) > 0) return Number(v);
  const mid = estimatedMidMrr(opp);
  return mid > 0 ? mid : 0;
}

/** Prefer verified peer growth; else blend radar momentum + demand so rows aren't identical. */
export function effectiveGrowthPct(opp) {
  const g = opp?.display_growth_30d ?? opp?.market_avg_growth_30d;
  if (g != null && Number.isFinite(Number(g))) return Number(g);
  const mom = (Number(opp?.momentum) || 0) * 100;
  const ts = Number(opp?.trend_score);
  if (!Number.isFinite(ts)) return mom;
  // trend_score is typically ~35–90; map to a %-like contribution centered near 50
  const demandAdj = (ts - 48) * 0.65;
  return mom * 0.45 + demandAdj * 0.55;
}

/**
 * Hot >25%, Rising 10–25%, Stable 0–10%, Cooling <0%.
 * @returns {'hot'|'rising'|'stable'|'cooling'}
 */
export function growthTierFromGrowthPct(growthPct) {
  const g = Number(growthPct);
  if (!Number.isFinite(g)) return "stable";
  if (g > 25) return "hot";
  if (g > 10) return "rising";
  if (g >= 0) return "stable";
  return "cooling";
}

export function growthTierForOpportunity(opp) {
  return growthTierFromGrowthPct(effectiveGrowthPct(opp));
}

/**
 * Internal ranking score — weights: avgMRR 0.4, growth 0.3, signals 0.2, momentum 0.1
 */
export function rankingScore(opp) {
  const mrr = effectiveMrrUsd(opp);
  const normMrr = clamp01(Math.log1p(Math.max(0, mrr)) / Math.log1p(250_000));

  const g = effectiveGrowthPct(opp);
  const normGrowth = clamp01((Math.max(-40, Math.min(120, g)) + 40) / 160);

  const sig = Number(opp?.signal_count) || 0;
  const normSig = clamp01(Math.log1p(sig) / Math.log1p(120));

  const mom = Number(opp?.momentum) || 0;
  const normMom = clamp01(mom);

  return 0.4 * normMrr + 0.3 * normGrowth + 0.2 * normSig + 0.1 * normMom;
}

export function formatAvgMrrDisplay(opp) {
  const usd = opp?.display_mrr_usd ?? opp?.market_avg_mrr_usd;
  if (usd != null && Number(usd) > 0) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(usd));
  }
  const min = Number(opp?.estimated_mrr_min) || 0;
  const max = Number(opp?.estimated_mrr_max) || 0;
  if (min > 0 && max > 0) {
    if (Math.round(min) === Math.round(max)) {
      return `~€${Math.round(min).toLocaleString()} / mo est.`;
    }
    return `€${Math.round(min).toLocaleString()}–${Math.round(max).toLocaleString()} / mo est.`;
  }
  return "—";
}

export function formatGrowthDisplay(opp) {
  const g = effectiveGrowthPct(opp);
  if (!Number.isFinite(g)) return "—";
  const sign = g > 0 ? "+" : "";
  return `${sign}${g.toFixed(1)}%`;
}

/** One-line compact MRR for dense cards (e.g. $2.8k or €5k–25k est.). */
export function formatMrrCompact(opp) {
  const usd = opp?.display_mrr_usd ?? opp?.market_avg_mrr_usd;
  if (usd != null && Number(usd) > 0) {
    const n = Number(usd);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1000) {
      const k = n / 1000;
      return `$${k >= 10 ? k.toFixed(0) : k.toFixed(1)}k`;
    }
    return `$${Math.round(n)}`;
  }
  const min = Number(opp?.estimated_mrr_min) || 0;
  const max = Number(opp?.estimated_mrr_max) || 0;
  if (min > 0 && max > 0) {
    const fmt = (v) => (v >= 1000 ? `€${Math.round(v / 1000)}k` : `€${Math.round(v)}`);
    if (Math.round(min) === Math.round(max)) return `${fmt(min)} est.`;
    return `${fmt(min)}–${fmt(max)} est.`;
  }
  return "—";
}
